import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { getDocumentSecurityAccess } from '../../config/documentSecurity'
import apiService, { documentAPI } from '../../services/api'
import { tokenStorage } from '../../utils/crypto'
import {
  HiOutlineDocumentText,
  HiOutlineEye,
  HiOutlineArrowDownTray,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineExclamationTriangle,
  HiOutlineCalendar,
  HiOutlineUser,
  HiOutlineScale,
  HiOutlineFolder,
  HiOutlineClock,
  HiOutlineArrowsUpDown,
  HiOutlineXMark,
  HiOutlinePlus,
  HiOutlineChatBubbleLeft,
  HiOutlineSpeakerWave,
  HiOutlineRectangleStack,
  HiOutlineCheckCircle
} from 'react-icons/hi2'
import ConfirmDialog from '../ui/ConfirmDialog'
import DocumentUploadModal from './DocumentUploadModal'
import ImportFromDriveButton from './ImportFromDriveButton'
import MessageViewModal from '../messages/MessageViewModal'
import BlockedAction from '../common/BlockedAction'

/**
 * DocumentTable Component
 * 
 * A comprehensive, reusable table for displaying documents with:
 * - Search, filters, sorting, pagination
 * - Relationship indicators (direct, through case, through session)
 * - Security level indicators
 * - Action buttons (view, download, edit, delete)
 * - Responsive design consistent with system UI/UX
 * 
 * @param {Object} props
 * @param {string} props.context - 'client', 'case', or 'session'
 * @param {number} props.contextId - ID of the client, case, or session
 * @param {string} props.contextName - Name/title for display
 * @param {function} props.onDocumentUpdate - Callback when document is updated/deleted
 * @param {object} props.uploadModalProps - Additional props for the upload modal (client, case, session info)
 */
const storageProviderLabel = (provider) => {
  if (provider === 'google_drive') return 'Drive'
  if (provider === 'onedrive') return 'OneDrive'
  if (provider === 'dropbox') return 'Dropbox'
  return provider
}

const DocumentTable = ({
  context, 
  contextId, 
  contextName, 
  onDocumentUpdate = () => {},
  uploadModalProps = {}
}) => {
  const { t } = useTranslation()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canUpdate, canDelete, hasPermission, userRole } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  // Refs for search input to prevent focus loss
  const searchInputRef = useRef(null)
  const searchTimeoutRef = useRef(null)

  // State management
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchInput, setSearchInput] = useState('') // Local input state for debouncing
  const [relationshipFilter, setRelationshipFilter] = useState('all') // Default to "all" documents
  const [securityFilter, setSecurityFilter] = useState('all')
  const [contentTypeFilter, setContentTypeFilter] = useState('all') // 'all', 'documents', 'messages'
  const [groupFilter, setGroupFilter] = useState('all') // 'all' or a group ID
  const [filterGroups, setFilterGroups] = useState([])
  const [groupSearchQuery, setGroupSearchQuery] = useState('')
  const [showGroupDropdown, setShowGroupDropdown] = useState(false)
  const groupDropdownRef = useRef(null)
  const [sortField, setSortField] = useState('created_at')
  const [sortDirection, setSortDirection] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedDocuments, setSelectedDocuments] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showGroupDetailModal, setShowGroupDetailModal] = useState(false)
  const [selectedGroupDetail, setSelectedGroupDetail] = useState(null)
  const [groupDocuments, setGroupDocuments] = useState([])
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false)
  const [joinGroupDoc, setJoinGroupDoc] = useState(null)
  const [availableGroups, setAvailableGroups] = useState([])
  const [joinGroupSearch, setJoinGroupSearch] = useState('')

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  })

  const itemsPerPage = 10

  // Permissions
  const permissions = {
    canView: hasPermission('document', 'read'),
    canEdit: canUpdate('document'),
    canDelete: canDelete('document')
  }

  // Security level access based on user role (using centralized config)
  const getDocumentSecurityAccessForRole = (documentSecurityLevel) => {
    // Get user role from permission context
    const role = userRole?.toLowerCase() || 'silver'
    
    // Use centralized security configuration
    return getDocumentSecurityAccess(role, documentSecurityLevel, permissions)
  }

  // Fetch groups for the "By Group" filter
  const fetchFilterGroups = useCallback(async () => {
    try {
      const params = {}
      if (isWorkspaceSwitched && ownerAccountId) {
        params.owner_account_id = ownerAccountId
      }
      const response = await apiService.get('/document-groups/', { params })
      if (response.success) {
        const responseData = response.data
        const groupsArray = Array.isArray(responseData)
          ? responseData
          : (responseData.data || responseData.results || [])
        setFilterGroups(groupsArray)
      }
    } catch (error) {
      console.error('Error fetching groups for filter:', error)
    }
  }, [isWorkspaceSwitched, ownerAccountId])

  useEffect(() => {
    fetchFilterGroups()
  }, [fetchFilterGroups])

  // Close group dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch all items (unified documents + messages)
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true)

      const params = {
        page: currentPage,
        page_size: itemsPerPage,
        ordering: `${sortDirection === 'desc' ? '-' : ''}${sortField}`
      }

      // Content type filter maps to backend 'type' param (supports comma-separated)
      if (contentTypeFilter === 'documents') {
        params.type = 'document'
      } else if (contentTypeFilter === 'messages') {
        params.type = 'text,voice'
      } else if (contentTypeFilter === 'text') {
        params.type = 'text'
      } else if (contentTypeFilter === 'voice') {
        params.type = 'voice'
      }
      // 'all' — no type filter, returns everything

      // Context-specific filtering
      if (context === 'client') {
        params.client_id = contextId
        if (relationshipFilter === 'direct') {
          params.case__isnull = 'true'
          params.session__isnull = 'true'
        } else if (relationshipFilter === 'case') {
          params.case__isnull = 'false'
          params.session__isnull = 'true'
        } else if (relationshipFilter === 'session') {
          params.session__isnull = 'false'
        }
      } else if (context === 'case') {
        params.case_id = contextId
        if (relationshipFilter === 'direct') {
          params.session__isnull = 'true'
        } else if (relationshipFilter === 'session') {
          params.session__isnull = 'false'
        }
      } else if (context === 'session') {
        params.session_id = contextId
      } else if (context === 'group') {
        params.group = contextId
        params.type = 'document' // Groups only contain documents
      }

      if (searchQuery) {
        params.search = searchQuery
      }
      if (securityFilter !== 'all') {
        params.security_level = securityFilter
      }
      if (groupFilter !== 'all') {
        params.group = groupFilter
      }
      if (isWorkspaceSwitched && ownerAccountId) {
        params.owner_account_id = ownerAccountId
      }

      const response = await documentAPI.list(params)

      if (response.success) {
        const responseData = response.data.data || response.data
        let itemsArray = responseData.data || responseData.results || []
        const pagination = responseData.pagination || {}

        // Filter based on security level access
        const accessibleItems = itemsArray.filter(item => {
          const securityAccess = getDocumentSecurityAccessForRole(item.security_level)
          return securityAccess.canSee
        })

        setItems(accessibleItems)
        // Use backend pagination counts if available, otherwise fall back to items length
        const backendTotal = pagination.total_count || pagination.count || responseData.count
        if (backendTotal !== undefined) {
          setTotalCount(backendTotal)
          setTotalPages(Math.ceil(backendTotal / itemsPerPage))
        } else {
          setTotalCount(accessibleItems.length)
          setTotalPages(Math.ceil(accessibleItems.length / itemsPerPage))
        }
      } else {
        showError(response.error?.message || `Failed to load items`)
      }
    } catch (error) {
      console.error('Error fetching items:', error)
      showError(`Failed to load items`)
    } finally {
      setLoading(false)
    }
  }, [context, contextId, currentPage, searchQuery, relationshipFilter, securityFilter, contentTypeFilter, groupFilter, sortField, sortDirection, isWorkspaceSwitched, ownerAccountId, itemsPerPage])

  // Fetch items when dependencies change
  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  // Debounced search - updates searchQuery after user stops typing
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set a new timeout to update the actual search query
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput)
    }, 300) // 300ms debounce

    // Cleanup on unmount or when searchInput changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchInput])

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, relationshipFilter, securityFilter, groupFilter, sortField, sortDirection, contentTypeFilter])

  // Document actions
  const handleViewDocument = async (doc) => {
    try {
      if (!doc?.secure_view_url) {
        showError('View URL not available')
        return
      }

      const token = tokenStorage.getToken()
      if (!token) {
        showError('Authentication required')
        return
      }

      const response = await fetch(doc.secure_view_url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/pdf'
        },
        credentials: 'include'
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
      } else {
        showError('Failed to load document for viewing')
      }
    } catch (error) {
      console.error('Error viewing document:', error)
      showError('Failed to open document')
    }
  }

  const handleDownloadDocument = async (doc) => {
    try {
      if (!doc?.secure_download_url) {
        showError('Download URL not available')
        return
      }

      const token = tokenStorage.getToken()
      if (!token) {
        showError('Authentication required')
        return
      }

      const response = await fetch(doc.secure_download_url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = doc.file_name || 'document'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)
        showSuccess(`${getEntity('document').singular} downloaded successfully`)
      } else {
        showError(`Failed to download ${getEntity('document').singular.toLowerCase()}`)
      }
    } catch (error) {
      console.error('Error downloading document:', error)
      showError(`Failed to download ${getEntity('document').singular.toLowerCase()}`)
    }
  }

  const handleDeleteDocument = async (documentId) => {
    try {
      const params = isWorkspaceSwitched && ownerAccountId
        ? { owner_account_id: ownerAccountId }
        : {}

      const response = await apiService.delete(`/documents/${documentId}/`, { params })

      if (response.success) {
        showSuccess(`${getEntity('document').singular} deleted successfully`)
        fetchItems()
        onDocumentUpdate()
        setSelectedDocuments(prev => prev.filter(id => id !== documentId))
      } else {
        showError(response.error?.message || `Failed to delete ${getEntity('document').singular.toLowerCase()}`)
      }
    } catch (error) {
      console.error('Error deleting document:', error)
      showError(`Failed to delete ${getEntity('document').singular.toLowerCase()}`)
    }
  }

  const confirmDeleteDocument = (doc) => {
    setConfirmDialog({
      isOpen: true,
      title: t('documents.deleteTitle', { entity: getEntity('document').singular }),
      message: t('documents.deleteMessage', { name: doc.file_name || doc.title || '' }),
      confirmText: t('common.delete'),
      onConfirm: () => {
        handleDeleteDocument(doc.id)
        setConfirmDialog({ ...confirmDialog, isOpen: false })
      },
      type: 'danger'
    })
  }

  const handleViewGroup = async (groupId, groupName) => {
    console.log('🔍 handleViewGroup called with:', { groupId, groupName })
    try {
      const params = isWorkspaceSwitched && ownerAccountId 
        ? { owner_account_id: ownerAccountId }
        : {}

      console.log('📡 Fetching group details for groupId:', groupId)
      
      // Fetch group details
      const groupResponse = await apiService.get(`/document-groups/${groupId}/`, { params })
      
      console.log('📦 Group response:', groupResponse)
      
      if (groupResponse.success) {
        // Try different possible paths for the group data
        const groupData = groupResponse.data?.data?.data || groupResponse.data?.data || groupResponse.data
        console.log('📋 Group data:', groupData)
        console.log('📋 Full group response structure:', JSON.stringify(groupResponse, null, 2))
        setSelectedGroupDetail(groupData)
        
        // Fetch documents in this group
        const docsResponse = await apiService.get('/documents/', {
          params: {
            ...params,
            group_id: groupId
          }
        })
        
        console.log('📄 Documents response:', docsResponse)
        
        if (docsResponse.success) {
          // Documents can be at different paths depending on pagination
          const docsData = docsResponse.data?.data?.data || docsResponse.data?.data?.results || docsResponse.data?.data || []
          console.log('✅ Setting groupDocuments:', docsData.length, 'documents')
          setGroupDocuments(docsData)
        }
        
        console.log('🎯 Opening modal, showGroupDetailModal will be:', true)
        console.log('🎯 selectedGroupDetail will be:', groupData)
        setShowGroupDetailModal(true)
      } else {
        console.error('❌ Group response failed:', groupResponse.error)
        showError(groupResponse.error?.message || 'Failed to load group details')
      }
    } catch (error) {
      console.error('❌ Error fetching group details:', error)
      showError('Failed to load group details')
    }
  }

  const handleRemoveDocumentFromGroup = async (documentId) => {
    try {
      const params = isWorkspaceSwitched && ownerAccountId 
        ? { owner_account_id: ownerAccountId }
        : {}

      const response = await apiService.patch(`/documents/${documentId}/`, 
        { group: null }, 
        { params }
      )
      
      if (response.success) {
        showSuccess(`${getEntity('document').singular} removed from group`)
        // Refresh group documents
        if (selectedGroupDetail) {
          handleViewGroup(selectedGroupDetail.id, selectedGroupDetail.name)
        }
        fetchItems()
      } else {
        showError(response.error?.message || `Failed to remove ${getEntity('document').singular.toLowerCase()} from group`)
      }
    } catch (error) {
      console.error('Error removing document from group:', error)
      showError(`Failed to remove ${getEntity('document').singular.toLowerCase()} from group`)
    }
  }

  const handleOpenJoinGroup = async (doc) => {
    setJoinGroupDoc(doc)
    try {
      const params = { page: 'all' }
      if (isWorkspaceSwitched && ownerAccountId) {
        params.owner_account_id = ownerAccountId
      }
      const response = await apiService.get('/document-groups/', { params })
      if (response.success) {
        // Handle both paginated ({data: [...]}) and non-paginated ([...]) response formats
        const responseData = response.data
        const groupsArray = Array.isArray(responseData)
          ? responseData
          : (responseData.data || responseData.results || [])
        setAvailableGroups(groupsArray)
      }
    } catch (error) {
      console.error('Error fetching groups:', error)
    }
    setShowJoinGroupModal(true)
  }

  const handleJoinGroup = async (groupId) => {
    if (!joinGroupDoc) return
    try {
      const params = isWorkspaceSwitched && ownerAccountId
        ? { owner_account_id: ownerAccountId }
        : {}
      const response = await apiService.patch(`/documents/${joinGroupDoc.id}/move-to-group/`, {
        group_id: parseInt(groupId)
      }, { params })

      if (response.success) {
        showSuccess(`${getEntity('document').singular} added to group successfully`)
        setShowJoinGroupModal(false)
        setJoinGroupDoc(null)
        fetchItems()
        onDocumentUpdate()
      } else {
        showError(response.error?.message || `Failed to add ${getEntity('document').singular.toLowerCase()} to group`)
      }
    } catch (error) {
      console.error('Error joining group:', error)
      showError(`Failed to add ${getEntity('document').singular.toLowerCase()} to group`)
    }
  }

  // Utility functions
  const getSecurityBadge = (level) => {
    const configs = {
      red: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: HiOutlineShieldExclamation,
        label: 'High'
      },
      yellow: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: HiOutlineExclamationTriangle,
        label: 'Medium'
      },
      green: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: HiOutlineShieldCheck,
        label: 'Low'
      }
    }

    const config = configs[level] || configs.green
    const Icon = config.icon

    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </div>
    )
  }

  const getRelationshipIndicator = (doc) => {
    if (context === 'client') {
      if (doc.session) {
        return (
          <div className="flex items-center gap-1 text-xs text-brand bg-brand-soft px-2 py-1 rounded-md">
            <HiOutlineCalendar className="w-3 h-3" />
            <span>via {getEntity('session').singular}</span>
          </div>
        )
      } else if (doc.case) {
        return (
          <div className="flex items-center gap-1 text-xs text-brand bg-info-soft px-2 py-1 rounded-md">
            <HiOutlineScale className="w-3 h-3" />
            <span>via {getEntity('case').singular}</span>
          </div>
        )
      } else {
        return (
          <div className="flex items-center gap-1 text-xs text-success bg-success-soft px-2 py-1 rounded-md">
            <HiOutlineUser className="w-3 h-3" />
            <span>Direct</span>
          </div>
        )
      }
    } else if (context === 'case') {
      if (doc.session) {
        return (
          <div className="flex items-center gap-1 text-xs text-brand bg-brand-soft px-2 py-1 rounded-md">
            <HiOutlineCalendar className="w-3 h-3" />
            <span>via {getEntity('session').singular}</span>
          </div>
        )
      } else {
        return (
          <div className="flex items-center gap-1 text-xs text-success bg-success-soft px-2 py-1 rounded-md">
            <HiOutlineScale className="w-3 h-3" />
            <span>Direct</span>
          </div>
        )
      }
    } else {
      return (
        <div className="flex items-center gap-1 text-xs text-success bg-success-soft px-2 py-1 rounded-md">
          <HiOutlineCalendar className="w-3 h-3" />
          <span>{getEntity('session').singular}</span>
        </div>
      )
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    if (isNaN(bytes) || bytes < 0) return 'Unknown'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    if (i >= sizes.length) return 'Size too large'

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleSelectDocument = (documentId) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    )
  }

  const handleSelectAll = () => {
    if (selectedDocuments.length === items.length && items.length > 0) {
      setSelectedDocuments([])
    } else {
      setSelectedDocuments(items.map(item => item.id))
    }
  }

  // Get relationship filter options based on context
  const getRelationshipFilterOptions = () => {
    if (context === 'client') {
      return [
        { value: 'direct', label: `Direct ${getEntity('document').plural}`, desc: `${getEntity('document').plural} directly attached to this ${getEntity('client').singular.toLowerCase()}` },
        { value: 'case', label: `${getEntity('case').singular} ${getEntity('document').plural}`, desc: `${getEntity('document').plural} attached through ${getEntity('client').singular.toLowerCase()} ${getEntity('case').plural.toLowerCase()}` },
        { value: 'session', label: `${getEntity('session').singular} ${getEntity('document').plural}`, desc: `${getEntity('document').plural} attached through ${getEntity('case').singular.toLowerCase()} ${getEntity('session').plural.toLowerCase()}` },
        { value: 'all', label: `All ${getEntity('document').plural}`, desc: `All ${getEntity('document').plural.toLowerCase()} related to this ${getEntity('client').singular.toLowerCase()}` }
      ]
    } else if (context === 'case') {
      return [
        { value: 'direct', label: `Direct ${getEntity('document').plural}`, desc: `${getEntity('document').plural} directly attached to this ${getEntity('case').singular.toLowerCase()}` },
        { value: 'session', label: `${getEntity('session').singular} ${getEntity('document').plural}`, desc: `${getEntity('document').plural} attached through ${getEntity('case').singular.toLowerCase()} ${getEntity('session').plural.toLowerCase()}` },
        { value: 'all', label: `All ${getEntity('document').plural}`, desc: `All ${getEntity('document').plural.toLowerCase()} related to this ${getEntity('case').singular.toLowerCase()}` }
      ]
    } else if (context === 'group') {
      return [
        { value: 'all', label: `All ${getEntity('document').plural}`, desc: `All ${getEntity('document').plural.toLowerCase()} in this group` }
      ]
    } else {
      return [
        { value: 'all', label: `${getEntity('session').singular} ${getEntity('document').plural}`, desc: `All ${getEntity('document').plural.toLowerCase()} in this ${getEntity('session').singular.toLowerCase()}` }
      ]
    }
  }

  // Loading state
  if (loading && items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="flex items-center justify-between">
              <div className="h-6 bg-gray-200 rounded w-48"></div>
              <div className="h-10 bg-gray-200 rounded w-32"></div>
            </div>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center gap-4 p-4 border border-gray-100 rounded-lg">
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="h-6 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 border-b border-gray-100">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {context === 'client' && `${getEntity('client').singular} ${getEntity('document').plural} & Messages`}
            {context === 'case' && `${getEntity('case').singular} ${getEntity('document').plural} & Messages`}
            {context === 'session' && `${getEntity('session').singular} ${getEntity('document').plural} & Messages`}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {context === 'client' && `${getEntity('document').plural} and messages directly attached to this ${getEntity('client').singular.toLowerCase()}, or through their ${getEntity('case').plural.toLowerCase()} and ${getEntity('session').plural.toLowerCase()}.`}
            {context === 'case' && `${getEntity('document').plural} and messages directly attached to this ${getEntity('case').singular.toLowerCase()}, or through its ${getEntity('session').plural.toLowerCase()}.`}
            {context === 'session' && `${getEntity('document').plural} and messages attached to this ${getEntity('session').singular.toLowerCase()}.`}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <ImportFromDriveButton
            caseId={uploadModalProps.caseId}
            sessionId={uploadModalProps.sessionId}
            clientId={uploadModalProps.clientId}
            onImported={() => {
              fetchItems()
              onDocumentUpdate()
            }}
          />
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <HiOutlinePlus className="w-4 h-4" />
            Upload {getEntity('document').singular}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 bg-white border-b border-gray-100 space-y-3">
        {/* Row 1: Type filter + Search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* By Type */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Type:</span>
            <button
              onClick={() => setContentTypeFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                contentTypeFilter === 'all'
                  ? 'bg-brand-soft text-brand border border-brand/30'
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              All Items
              <span className="text-xs bg-white/50 px-1.5 py-0.5 rounded">
                {items.length}
              </span>
            </button>
            <button
              onClick={() => setContentTypeFilter('documents')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                contentTypeFilter === 'documents'
                  ? 'bg-brand-soft text-brand border border-brand/30'
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              <HiOutlineDocumentText className="w-4 h-4" />
              {getEntity('document').plural}
              <span className="text-xs bg-white/50 px-1.5 py-0.5 rounded">{items.filter(i => i.type === 'document').length}</span>
            </button>
            <button
              onClick={() => setContentTypeFilter('messages')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                contentTypeFilter === 'messages'
                  ? 'bg-green-100 text-green-800 border border-success/30'
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              <HiOutlineChatBubbleLeft className="w-4 h-4" />
              Messages
              <span className="text-xs bg-white/50 px-1.5 py-0.5 rounded">{items.filter(i => i.type === 'text' || i.type === 'voice').length}</span>
            </button>
            <button
              onClick={() => setContentTypeFilter('text')}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                contentTypeFilter === 'text'
                  ? 'bg-green-100 text-green-800 border border-success/30'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              Text
              <span className="text-xs bg-white/50 px-1 py-0.5 rounded">{items.filter(i => i.type === 'text').length}</span>
            </button>
            <button
              onClick={() => setContentTypeFilter('voice')}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                contentTypeFilter === 'voice'
                  ? 'bg-info-soft text-info border border-info/30'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              <HiOutlineSpeakerWave className="w-3.5 h-3.5" />
              Voice
              <span className="text-xs bg-white/50 px-1 py-0.5 rounded">{items.filter(i => i.type === 'voice').length}</span>
            </button>
          </div>

          {/* By Source */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Source:</span>
            {getRelationshipFilterOptions().map(option => (
              <button
                key={option.value}
                onClick={() => setRelationshipFilter(option.value)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  relationshipFilter === option.value
                    ? 'bg-info-soft text-brand border border-info/30'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
                title={option.desc}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Search and Filters Toggle */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={`Search ${getEntity('document').plural.toLowerCase()}...`}
                className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
                autoComplete="off"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('')
                    setSearchQuery('')
                    searchInputRef.current?.focus()
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <HiOutlineXMark className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                showFilters 
                  ? 'bg-brand-soft text-brand border border-brand/30' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              <HiOutlineAdjustmentsHorizontal className="w-4 h-4" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Additional Filters */}
      {showFilters && (
        <div className="p-4 bg-gray-50 border-b border-gray-100">
          <div className="flex flex-wrap gap-4">
            {/* Security Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Security Level</label>
              <select
                value={securityFilter}
                onChange={(e) => setSecurityFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand"
              >
                <option value="all">All Security Levels</option>
                <option value="green">🟢 Low Security</option>
                <option value="yellow">🟡 Medium Security</option>
                <option value="red">🔴 High Security</option>
              </select>
            </div>

            {/* By Group Filter */}
            <div className="relative" ref={groupDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">By Group</label>
              <button
                type="button"
                onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand text-left flex items-center justify-between gap-2"
              >
                <span className="truncate text-sm">
                  {groupFilter === 'all'
                    ? `All ${getEntity('document').plural}`
                    : filterGroups.find(g => String(g.id) === String(groupFilter))?.name || 'Select group'}
                </span>
                <HiOutlineFolder className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
              {showGroupDropdown && (
                <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <HiOutlineMagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={groupSearchQuery}
                        onChange={(e) => setGroupSearchQuery(e.target.value)}
                        placeholder="Search groups..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-brand/30 focus:border-brand"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => { setGroupFilter('all'); setShowGroupDropdown(false); setGroupSearchQuery('') }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-brand-soft ${groupFilter === 'all' ? 'bg-brand-soft text-brand font-medium' : 'text-gray-700'}`}
                    >
                      All {getEntity('document').plural}
                    </button>
                    {filterGroups
                      .filter(g => g.name.toLowerCase().includes(groupSearchQuery.toLowerCase()))
                      .map(group => (
                        <button
                          key={group.id}
                          type="button"
                          onClick={() => { setGroupFilter(String(group.id)); setShowGroupDropdown(false); setGroupSearchQuery('') }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-brand-soft ${String(groupFilter) === String(group.id) ? 'bg-brand-soft text-brand font-medium' : 'text-gray-700'}`}
                        >
                          <span className="flex items-center gap-2">
                            <HiOutlineFolder className="w-4 h-4 text-gray-400" />
                            {group.name}
                          </span>
                        </button>
                      ))
                    }
                    {filterGroups.filter(g => g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">No groups found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Results Summary */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 text-sm text-gray-600">
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin"></div>
            Loading {getEntity('document').plural.toLowerCase()}...
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span>
              Showing {items.length} of {totalCount} items
              {searchInput && ` matching "${searchInput}"`}
            </span>
            {selectedDocuments.length > 0 && (
              <span className="font-medium">
                {selectedDocuments.length} selected
              </span>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-4 p-4">
                <input
                  type="checkbox"
                  checked={selectedDocuments.length === items.length && items.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-brand focus:ring-brand/30"
                />
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('file_name')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Content
                  <HiOutlineArrowsUpDown className="w-4 h-4" />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-gray-700">Source</span>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-gray-700">Security</span>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('file_size')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Size
                  <HiOutlineArrowsUpDown className="w-4 h-4" />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('created_at')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Uploaded
                  <HiOutlineArrowsUpDown className="w-4 h-4" />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-gray-700">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-12 text-center">
                  <div className="flex flex-col items-center">
                    <HiOutlineDocumentText className="w-12 h-12 text-gray-300 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      No {contentTypeFilter === 'all' ? 'items' : contentTypeFilter} found
                    </h4>
                    <p className="text-gray-500">
                      {searchQuery
                        ? `No ${contentTypeFilter === 'all' ? 'items' : contentTypeFilter} match your search criteria.`
                        : `No ${contentTypeFilter === 'all' ? 'items' : contentTypeFilter} are attached to this ${context}.`
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={`${item.type}-${item.id}`} className="hover:bg-gray-50">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedDocuments.includes(item.id)}
                      onChange={() => handleSelectDocument(item.id)}
                      className="rounded border-gray-300 text-brand focus:ring-brand/30"
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {item.type === 'document' ? (
                        <>
                          {/* Show different icon for grouped vs single documents */}
                          {item.group ? (
                            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center" title={`Part of group: ${item.group_name || 'Unnamed Group'}`}>
                              <HiOutlineRectangleStack className="w-4 h-4 text-indigo-600" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center" title="Single document">
                              <HiOutlineDocumentText className="w-4 h-4 text-brand" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              <span>{item.title || item.file_name}</span>
                              {item.storage_provider && item.storage_provider !== 'local' && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-info-soft text-brand border border-info/30"
                                  title={`Stored on ${storageProviderLabel(item.storage_provider)}`}
                                >
                                  {storageProviderLabel(item.storage_provider)}
                                </span>
                              )}
                              {item.storage_missing && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-danger-soft text-danger border border-danger/30"
                                  title="This file can't be opened from the connected cloud storage. Either it was deleted directly in Google Drive, or it was stored by a previous OVACS connection and needs re-linking (Settings → Plan & Storage → Re-link files)."
                                >
                                  Missing from Drive
                                </span>
                              )}
                            </div>
                            {/* Show group name if document is in a group - clickable badge */}
                            {item.group && item.group_name && (
                              <button
                                onClick={(e) => {
                                  console.log('🖱️ Badge clicked!', { group: item.group, name: item.group_name })
                                  e.stopPropagation()
                                  handleViewGroup(item.group, item.group_name)
                                }}
                                className="text-xs text-indigo-600 flex items-center gap-1 hover:text-indigo-800 hover:underline transition-colors cursor-pointer"
                                title="Click to view group details"
                              >
                                <HiOutlineRectangleStack className="w-3 h-3" />
                                {item.group_name}
                              </button>
                            )}
                            {item.title && item.file_name && item.title !== item.file_name && (
                              <div className="text-xs text-gray-500">
                                {item.file_name}
                              </div>
                            )}
                            {!item.title && !item.group_name && (
                              <div className="text-xs text-gray-400">No document name</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            item.type === 'voice' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            {item.type === 'voice' ? (
                              <HiOutlineSpeakerWave className="w-4 h-4 text-brand" />
                            ) : (
                              <HiOutlineChatBubbleLeft className="w-4 h-4 text-success" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              {item.title || (item.type === 'voice' ? 'Voice Message' : 'Text Message')}
                              <span className="text-xs text-gray-500">by {item.created_by_name}</span>
                            </div>
                            {item.type === 'text' && item.content && (
                              <div className="text-xs text-gray-600 truncate max-w-xs">
                                {item.content.substring(0, 60)}{item.content.length > 60 ? '...' : ''}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      {getRelationshipIndicator(item)}
                      {item.case_title && item.case_title !== contextName && (
                        <div className="text-xs text-gray-500">
                          {getEntity('case').singular}: {item.case_title}
                        </div>
                      )}
                      {item.session_title && (
                        <div className="text-xs text-gray-500">
                          {getEntity('session').singular}: {item.session_title}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    {getSecurityBadge(item.security_level)}
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-gray-900">
                      {item.type === 'voice' ? '🎤 Audio' :
                        item.file_size ? formatFileSize(item.file_size) :
                        item.type === 'text' ? '💬 Text' : '—'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-gray-900">
                      {formatDate(item.created_at)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      {item.type === 'document' ? (
                        (() => {
                          const securityAccess = getDocumentSecurityAccessForRole(item.security_level)
                          return (
                            <>
                              <BlockedAction action="view" badgeSize="sm">
                                <button
                                  onClick={() => securityAccess.canView ? handleViewDocument(item) : null}
                                  disabled={!securityAccess.canView}
                                  className={`p-2 rounded-lg transition-colors ${
                                    securityAccess.canView
                                      ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                      : 'text-gray-300 cursor-not-allowed opacity-50'
                                  }`}
                                  title={securityAccess.canView ? "View document" : securityAccess.restrictionMessage}
                                >
                                  <HiOutlineEye className="w-4 h-4" />
                                </button>
                              </BlockedAction>

                              <BlockedAction action="download" badgeSize="sm">
                                <button
                                  onClick={() => securityAccess.canDownload ? handleDownloadDocument(item) : null}
                                  disabled={!securityAccess.canDownload}
                                  className={`p-2 rounded-lg transition-colors ${
                                    securityAccess.canDownload
                                      ? 'text-gray-400 hover:text-brand hover:bg-info-soft'
                                      : 'text-gray-300 cursor-not-allowed opacity-50'
                                  }`}
                                  title={securityAccess.canDownload ? "Download document" : securityAccess.restrictionMessage}
                                >
                                  <HiOutlineArrowDownTray className="w-4 h-4" />
                                </button>
                              </BlockedAction>

                              {securityAccess.canEdit ? (
                                <button
                                  onClick={() => window.open(`/documents/${item.id}`, '_blank')}
                                  className="text-gray-400 hover:text-success p-2 rounded-lg hover:bg-success-soft transition-colors"
                                  title="View details"
                                >
                                  <HiOutlinePencil className="w-4 h-4" />
                                </button>
                              ) : permissions.canEdit && (
                                <button
                                  disabled={true}
                                  className="text-gray-300 cursor-not-allowed opacity-50 p-2 rounded-lg"
                                  title={securityAccess.restrictionMessage}
                                >
                                  <HiOutlinePencil className="w-4 h-4" />
                                </button>
                              )}

                              {/* Join Group button for ungrouped documents */}
                              {!item.group && permissions.canEdit && (
                                <button
                                  onClick={() => handleOpenJoinGroup(item)}
                                  className="text-gray-400 hover:text-indigo-600 p-2 rounded-lg hover:bg-indigo-50 transition-colors"
                                  title="Join a group"
                                >
                                  <HiOutlineFolder className="w-4 h-4" />
                                </button>
                              )}

                              {securityAccess.canDelete ? (
                                <button
                                  onClick={() => confirmDeleteDocument(item)}
                                  className="text-gray-400 hover:text-danger p-2 rounded-lg hover:bg-danger-soft transition-colors"
                                  title="Delete document"
                                >
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              ) : permissions.canDelete && (
                                <button
                                  disabled={true}
                                  className="text-gray-300 cursor-not-allowed opacity-50 p-2 rounded-lg"
                                  title={securityAccess.restrictionMessage}
                                >
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )
                        })()
                      ) : (
                        /* Message actions (text/voice) */
                        (() => {
                          const securityAccess = getDocumentSecurityAccessForRole(item.security_level)
                          return (
                            <>
                              {/* Read status indicator */}
                              {item.is_read === false && (
                                <span className="w-2 h-2 bg-info-soft0 rounded-full" title="Unread" />
                              )}

                              <button
                                onClick={() => {
                                  setSelectedMessage(item)
                                  setShowMessageModal(true)
                                }}
                                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                title="View message"
                              >
                                <HiOutlineEye className="w-4 h-4" />
                              </button>

                              {item.type === 'voice' && item.voice_download_url && (
                                <button
                                  onClick={() => window.open(item.voice_download_url, '_blank')}
                                  className="text-gray-400 hover:text-brand p-2 rounded-lg hover:bg-info-soft transition-colors"
                                  title="Download voice"
                                >
                                  <HiOutlineArrowDownTray className="w-4 h-4" />
                                </button>
                              )}

                              {item.type === 'text' && item.secure_download_url && securityAccess.canDownload && (
                                <button
                                  onClick={() => handleDownloadDocument(item)}
                                  className="text-gray-400 hover:text-brand p-2 rounded-lg hover:bg-info-soft transition-colors"
                                  title="Download as .txt"
                                >
                                  <HiOutlineArrowDownTray className="w-4 h-4" />
                                </button>
                              )}

                              {/* Mark as read */}
                              {item.is_read === false && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await documentAPI.markAsRead(item.id)
                                      fetchItems()
                                    } catch (e) { console.error(e) }
                                  }}
                                  className="text-gray-400 hover:text-success p-2 rounded-lg hover:bg-success-soft transition-colors"
                                  title="Mark as read"
                                >
                                  <HiOutlineCheckCircle className="w-4 h-4" />
                                </button>
                              )}

                              {/* Join Group */}
                              {!item.group && permissions.canEdit && (
                                <button
                                  onClick={() => handleOpenJoinGroup(item)}
                                  className="text-gray-400 hover:text-indigo-600 p-2 rounded-lg hover:bg-indigo-50 transition-colors"
                                  title="Join a group"
                                >
                                  <HiOutlineFolder className="w-4 h-4" />
                                </button>
                              )}

                              {/* Delete */}
                              {securityAccess.canDelete && (
                                <button
                                  onClick={() => confirmDeleteDocument(item)}
                                  className="text-gray-400 hover:text-danger p-2 rounded-lg hover:bg-danger-soft transition-colors"
                                  title="Delete"
                                >
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )
                        })()
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 border-t border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
            <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalCount)}</span> of{' '}
            <span className="font-medium">{totalCount}</span> {getEntity('document').plural.toLowerCase()}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <HiOutlineChevronLeft className="w-4 h-4" />
              Previous
            </button>
            
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">Page</span>
              <span className="text-sm font-medium text-gray-900">{currentPage}</span>
              <span className="text-sm text-gray-600">of</span>
              <span className="text-sm font-medium text-gray-900">{totalPages}</span>
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <HiOutlineChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          fetchItems()
          onDocumentUpdate()
          setShowUploadModal(false)
        }}
        preselectedClient={uploadModalProps.clientId}
        preselectedCase={uploadModalProps.caseId}
        preselectedSession={uploadModalProps.sessionId}
        disablePreselectedFields={true}
      />

      {/* Message View Modal */}
      <MessageViewModal
        isOpen={showMessageModal}
        onClose={() => {
          setShowMessageModal(false)
          setSelectedMessage(null)
        }}
        message={selectedMessage}
      />

      {/* Group Detail Modal */}
      {console.log('🎨 Rendering check - showGroupDetailModal:', showGroupDetailModal, 'selectedGroupDetail:', selectedGroupDetail)}
      {showGroupDetailModal && selectedGroupDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
             onClick={(e) => console.log('🎨 Modal overlay clicked')}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Group Details</h3>
              <button
                onClick={() => setShowGroupDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{selectedGroupDetail.name}</h4>
                <p className="text-gray-600 text-sm">{selectedGroupDetail.description || 'No description'}</p>
                <div className="mt-2 text-sm text-gray-500 space-y-1">
                  {selectedGroupDetail.client_name && (
                    <div>{getEntity('client').singular}: {selectedGroupDetail.client_name}</div>
                  )}
                  {selectedGroupDetail.case_title && (
                    <div>{getEntity('case').singular}: {selectedGroupDetail.case_title}</div>
                  )}
                  {selectedGroupDetail.session_title && (
                    <div>{getEntity('session').singular}: {selectedGroupDetail.session_title}</div>
                  )}
                  <div>{groupDocuments.length} {groupDocuments.length !== 1 ? getEntity('document').plural.toLowerCase() : getEntity('document').singular.toLowerCase()}</div>
                </div>
              </div>

              <div className="space-y-3">
                <h5 className="font-medium text-gray-900">{getEntity('document').plural} in this group</h5>
                {groupDocuments.length > 0 ? (
                  <div className="space-y-2">
                    {groupDocuments.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <HiOutlineDocumentText className="w-4 h-4 text-brand" />
                          <div>
                            <div className="font-medium text-gray-900">{doc.title || doc.file_name}</div>
                            <div className="text-sm text-gray-500">{formatFileSize(doc.file_size)}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {(() => {
                            const securityAccess = getDocumentSecurityAccessForRole(doc.security_level)
                            return (
                              <>
                                <button
                                  onClick={() => securityAccess.canView ? handleViewDocument(doc) : null}
                                  disabled={!securityAccess.canView}
                                  className={`p-2 rounded-lg transition-colors ${
                                    securityAccess.canView
                                      ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                      : 'text-gray-300 cursor-not-allowed opacity-50'
                                  }`}
                                  title={securityAccess.canView ? "View document" : securityAccess.restrictionMessage}
                                >
                                  <HiOutlineEye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => securityAccess.canDownload ? handleDownloadDocument(doc) : null}
                                  disabled={!securityAccess.canDownload}
                                  className={`p-2 rounded-lg transition-colors ${
                                    securityAccess.canDownload
                                      ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                      : 'text-gray-300 cursor-not-allowed opacity-50'
                                  }`}
                                  title={securityAccess.canDownload ? "Download document" : securityAccess.restrictionMessage}
                                >
                                  <HiOutlineArrowDownTray className="w-4 h-4" />
                                </button>
                                {permissions.canEdit && securityAccess.canEdit && (
                                  <button
                                    onClick={() => handleRemoveDocumentFromGroup(doc.id)}
                                    className="text-gray-400 hover:text-danger p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                    title="Remove from group"
                                  >
                                    <HiOutlineXMark className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <HiOutlineDocumentText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No {getEntity('document').plural.toLowerCase()} in this group</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end items-center gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowGroupDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Join Group Modal */}
      {showJoinGroupModal && joinGroupDoc && (() => {
        const filteredGroups = availableGroups.filter(g =>
          g.name.toLowerCase().includes(joinGroupSearch.toLowerCase())
        )
        return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => { setShowJoinGroupModal(false); setJoinGroupDoc(null); setJoinGroupSearch('') }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-5 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
                    <HiOutlineFolder className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Join Group</h3>
                    <p className="text-xs text-gray-400 truncate max-w-[200px]">
                      {joinGroupDoc.title || joinGroupDoc.file_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowJoinGroupModal(false); setJoinGroupDoc(null); setJoinGroupSearch('') }}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <HiOutlineXMark className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 py-3">
              {availableGroups.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <HiOutlineFolder className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">No groups available</p>
                  <p className="text-xs text-gray-400 mt-0.5">Create a group first</p>
                </div>
              ) : (
                <>
                  {/* Search */}
                  {availableGroups.length > 4 && (
                    <div className="relative mb-2">
                      <HiOutlineMagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search groups..."
                        value={joinGroupSearch}
                        onChange={e => setJoinGroupSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 placeholder-gray-400"
                        autoFocus
                      />
                    </div>
                  )}

                  {/* Group List */}
                  <div className="max-h-48 overflow-y-auto -mx-1 px-1 space-y-1 scrollbar-thin">
                    {filteredGroups.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">No matching groups</p>
                    ) : (
                      filteredGroups.map(group => (
                        <button
                          key={group.id}
                          onClick={() => handleJoinGroup(group.id)}
                          className="w-full px-3 py-2 text-left rounded-lg hover:bg-indigo-50 transition-all group/item flex items-center gap-2.5"
                        >
                          <div className="w-7 h-7 bg-indigo-50 group-hover/item:bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                            <HiOutlineFolder className="w-3.5 h-3.5 text-indigo-500" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-800 truncate group-hover/item:text-indigo-700 transition-colors">{group.name}</div>
                            {group.description && (
                              <div className="text-[11px] text-gray-400 truncate">{group.description}</div>
                            )}
                          </div>
                          <HiOutlineChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover/item:text-indigo-400 flex-shrink-0 transition-colors" />
                        </button>
                      ))
                    )}
                  </div>

                  {/* Count */}
                  <div className="mt-2 pt-2 border-t border-gray-50">
                    <p className="text-[11px] text-gray-400 text-center">
                      {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 pb-4">
              <button
                onClick={() => { setShowJoinGroupModal(false); setJoinGroupDoc(null); setJoinGroupSearch('') }}
                className="w-full px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        )
      })()}
    </div>
  )
}

export default DocumentTable
